import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Loader } from "../../../global/components/loader";
import { useAppSelector } from "../app/hooks";
import { BestAvailableState } from "../data/interfaces/types";
import bestAvailableModelSlice from "../data/slices/best-available-model-slice";
import { ConfirmationModal } from "./confirmation-modal";
import { MiniCart } from "./mini-cart";
import { PerformanceDropdown } from "./performance-dropdown";
import { BAPriceTypeQuantity } from "./price-type-quantity";
import { ProductionCard } from "./production-card";
import { ActivityType } from "./wheelchair-activity-type";
import { ZoneDropdown } from "./zone-dropdown";

export default function BestAvailableApp() {
    const bestAvailableModel = useAppSelector((state: BestAvailableState) => state.bestAvailableModel);
    const bestAvailableText = useAppSelector((state: BestAvailableState) => state.bestAvailableText);
    const calendarLoadingLabel = "Test";
    const [currentModel, setCurrentModel] = useState(bestAvailableModel);
    const miniCartModel = useAppSelector((state: BestAvailableState) => state.miniCartModel);
    const [currentMiniCartModel, setCurrentMiniCartModel] = useState(miniCartModel);
    const loading = useAppSelector((state: BestAvailableState) => state.loading);
    const [isLoading, setIsLoading] = useState(loading);
    const dispatch = useDispatch();

    useEffect(() => { setCurrentModel(bestAvailableModel) }, [bestAvailableModel])
    useEffect(() => { setCurrentMiniCartModel(miniCartModel) }, [miniCartModel]);
    useEffect(() => { setIsLoading(loading) }, [loading]);

    const updateShowModal = (showModal: boolean) => {
        dispatch(bestAvailableModelSlice.actions.updateShowWheelchairModal(showModal));
    }

    const currencyFormatter = (amount: number): string => {
        var formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        });

        return formatter.format(amount);
    }

    const pwycMinimumHtml = React.useMemo(() => {
        const template = bestAvailableText?.pwycMinimumPriceMessaging;
        if (!template) return "";

        const selectedZone = bestAvailableModel?.zoneInformation?.find(
            z => z.id === bestAvailableModel.selectedZoneId
        );
        if (!selectedZone?.isPwyc) return "";

        const zonePtQuantities = bestAvailableModel?.priceTypeQuantities?.filter(
            pt => pt.zoneId === bestAvailableModel.selectedZoneId && pt.isPwyc
        );
        const minAmount = zonePtQuantities?.length
            ? Math.min(...zonePtQuantities.map(pt => pt.pwycMinAmount))
            : bestAvailableModel?.currentPerformance?.pwycMinAmount ?? 0;

        return template.replace("{0}", String(currencyFormatter(minAmount)));
    }, [
        bestAvailableText?.pwycMinimumPriceMessaging,
        bestAvailableModel?.currentPerformance?.pwycMinAmount,
        bestAvailableModel?.selectedZoneId,
        bestAvailableModel?.zoneInformation,
        bestAvailableModel?.priceTypeQuantities
    ]);

    return (
        <>
            {
                isLoading &&
                <Loader calendarLoadingLabel={calendarLoadingLabel} loaderImageUrl={bestAvailableModel.loaderImageUrl} />
            }
            <div className="ace-layout ace-layout-8-4 ace-bestavailable-layout">

                {
                    !isLoading &&
                    <div>
                        <ProductionCard />
                        <div className="preview-card-panel">
                            {
                                currentModel.showErrorMessage ?
                                    <div className="rich-text" dangerouslySetInnerHTML={{ __html: currentModel.errorMessage }} />
                                    :
                                    <div className="card theme-light">
                                        <div className="stack-lg">
                                            {
                                                pwycMinimumHtml &&
                                                <div className="rich-text" dangerouslySetInnerHTML={{ __html: pwycMinimumHtml }} />
                                            }

                                            {
                                                currentModel.production != undefined && (currentModel.production.venueImageUrl !== ""
                                                    && currentModel.production.venueImageUrl != undefined) &&
                                                <div>
                                                    <img src={currentModel.production.venueImageUrl} alt={currentModel.production.venueDescription} />
                                                </div>
                                            }
                                            {
                                                currentModel.performances != null && currentModel.performances?.length > 0 &&
                                                <PerformanceDropdown />
                                            }
                                            {
                                                !currentModel.showErrorMessage && !currentModel.isGeneralAdmission &&
                                                <ZoneDropdown />
                                            }
                                            {
                                                !currentModel.showErrorMessage && currentModel.priceTypeQuantities != null && currentModel.priceTypeQuantities?.length > 0 &&
                                                <ul className="stack-sm ace-bestavailable-ticketamount">
                                                    {
                                                        currentModel.priceTypeQuantities.map((ptQuantity, index) => {
                                                            return <BAPriceTypeQuantity index={index} ptQuantity={ptQuantity} />
                                                        })
                                                    }
                                                </ul>
                                            }
                                            {
                                                !currentModel.isGeneralAdmission && currentModel.redirectToSmartSeat &&
                                                currentMiniCartModel.priceTypeQuantities.length > 0 &&
                                                <div>
                                                    <input onChange={(e) => {
                                                        updateShowModal(e.target.checked)
                                                    }} type="checkbox" defaultChecked={currentModel.showWheelchairModal} checked={currentModel.showWheelchairModal} id="wheelchairSeating" value={currentModel.showWheelchairModal.toString()} />
                                                    <label htmlFor="wheelchairSeating">
                                                        {bestAvailableText.checkboxText}
                                                    </label>
                                                    {
                                                        bestAvailableText.wheelchairCheckboxDescription &&
                                                        <div className="rich-text" dangerouslySetInnerHTML={{ __html: bestAvailableText.wheelchairCheckboxDescription }} />
                                                    }
                                                    {
                                                        currentModel.showWheelchairModal &&
                                                        <ConfirmationModal />
                                                    }
                                                </div>
                                            }
                                            {
                                                currentModel.isGeneralAdmission && currentModel.activityTypes != null &&
                                                currentModel.activityTypes?.length > 0 && currentModel.showWheelchairActivityTypes &&
                                                <ul className="stack-sm">
                                                    {
                                                        currentModel.activityTypes.map((activityType, index) => {
                                                            return <ActivityType index={index} activityType={activityType} />
                                                        })
                                                    }
                                                </ul>
                                            }
                                        </div>
                                    </div>
                            }

                        </div>
                    </div>
                }
                {
                    currentMiniCartModel != null && !isLoading &&
                    <MiniCart />
                }
            </div>
        </>
    );
}